import React, { useRef } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Table } from "react-bootstrap";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const utahData = "/img/UTDT_Logo.png";
const bodex = "/img/bodexLogo.png";
const kpi = "/img/KPI-DASH 2.png";
const pdfIcon = "/img/pdf1.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

export default function CsAndItTable6({ data }) {
  const pdfRef = useRef();

  if (!data || data.length === 0) return <p>No data available</p>;

  const ageGroups = data.map((item) => item.age_group_label);

  const femaleShare = data.map((item) => parseFloat(item.female_share_2024));
  const maleShare = data.map((item) => parseFloat(item.male_share_2024));

  // Summary Metrics
  const highestFemale = Math.max(...femaleShare);
  const highestFemaleGroup = ageGroups[femaleShare.indexOf(highestFemale)];

  const highestMale = Math.max(...maleShare);
  const highestMaleGroup = ageGroups[maleShare.indexOf(highestMale)];

  const avgFemale = (
    femaleShare.reduce((a, b) => a + b, 0) / femaleShare.length
  ).toFixed(1);
  const avgMale = (
    maleShare.reduce((a, b) => a + b, 0) / maleShare.length
  ).toFixed(1);

  const chartData = {
    labels: ageGroups,
    datasets: [
      {
        label: "Female Share (%)",
        data: femaleShare,
        backgroundColor: "#EC4899",
        borderColor: "#EC4899",
        borderWidth: 1,
        stack: "stack",
      },
      {
        label: "Male Share (%)",
        data: maleShare,
        backgroundColor: "#0ea5e9",
        borderColor: "#0ea5e9",
        borderWidth: 1,
        stack: "stack",
      },
    ],
  };

  const chartOptions = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "Employment Share by Age Group (2024)",
        font: { size: 16, weight: "bold" },
      },
    },
    scales: {
      x: {
        stacked: true,
        max: 100,
        title: {
          display: true,
          text: "Percentage (%)",
          font: { size: 14, weight: "bold" },
        },
        ticks: { callback: (v) => `${v}%` },
      },
      y: {
        stacked: true,
        title: { display: true, text: "Age Group" },
      },
    },
  };

  // ---- PDF Export ----
  const downloadPDF = () => {
    const input = pdfRef.current;
    html2canvas(input, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      scrollY: -window.scrollY,
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth - 10;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;
      let pageNumber = 1;

      const addHeader = () => {
        const logoWidth = 20; // ideal header width
        const logoHeight = 20; // adjust ratio if needed

        pdf.addImage(utahData, "PNG", 70, 10, logoWidth, logoHeight);
        pdf.setFontSize(26);
        pdf.text("UTahData.org", 95, 22);
        pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
        pdf.line(10, 35, 200, 35);
      };

      const addFooter = (pageNumber) => {
        pdf.setFontSize(9);
        const footerY = pageHeight - 10;
        pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
        pdf.text(
          `Downloaded on: ${new Date().toLocaleDateString()}`,
          10,
          footerY,
        );
        pdf.text(
          `Page ${pageNumber}`,
          pageWidth - 10,
          footerY,
          null,
          null,
          "right",
        );
      };

      // First page
      addHeader();
      pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
      heightLeft -= pageHeight - 32;

      // Add more pages if needed
      while (heightLeft > 0) {
        pdf.addPage();
        pageNumber++;
        addHeader();
        position = heightLeft - imgHeight;
        pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
        heightLeft -= pageHeight - 32;
        addFooter(pageNumber);
      }

      // Final footer
      // ===== FINAL FOOTER SAFE AREA =====

      const footerTopY = pageHeight - 40; // start footer 40mm from bottom
      const footerContentY = pageHeight - 28; // logos row
      const footerBottomTextY = pageHeight - 10; // bottom text row

      // Top Divider Line
      pdf.line(10, footerTopY, pageWidth - 10, footerTopY);

      // UtahData logo
      const utahWidth = 18;
      const utahHeight = 18;
      pdf.addImage(
        utahData,
        "PNG",
        20,
        footerContentY - 8,
        utahWidth,
        utahHeight,
      );

      // Built by
      pdf.setFontSize(10);
      pdf.text("Built by", 45, footerContentY);

      // BODEX logo
      const bodexWidth = 36;
      const bodexHeight = bodexWidth * 0.3;
      pdf.addImage(
        bodex,
        "PNG",
        60,
        footerContentY - 6,
        bodexWidth,
        bodexHeight,
      );

      // Using text
      pdf.text("using", 102, footerContentY);

      // KPI logo
      pdf.addImage(kpi, "PNG", 118, footerContentY - 8, 16, 16);

      // Version
      pdf.text("20250916.v3.198", 140, footerContentY);

      // Bottom Info Line
      pdf.setFontSize(8);
      pdf.text(`From: ${window.location.href}`, 10, footerBottomTextY);
      pdf.text(
        `Downloaded on: ${new Date().toLocaleDateString()}`,
        pageWidth - 10,
        footerBottomTextY,
        null,
        null,
        "right",
      );

      pdf.save("cs_it_age_group_share.pdf");
    });
  };

  return (
    <>
      <div ref={pdfRef} className="p-6 bg-white rounded-xl shadow-md">
        <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">
          Employment Share by Age Group (2024)
        </h2>

        {/* Summary Insight Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8 text-center">
          <div className="p-4 bg-blue-100 rounded-xl shadow">
            <p className="text-sm text-gray-600">Avg Female Share</p>
            <p className="text-xl font-bold text-blue-900">{avgFemale}%</p>
          </div>

          <div className="p-4 bg-red-100 rounded-xl shadow">
            <p className="text-sm text-gray-600">Avg Male Share</p>
            <p className="text-xl font-bold text-red-900">{avgMale}%</p>
          </div>

          <div className="p-4 bg-green-100 rounded-xl shadow">
            <p className="text-sm text-gray-600">Peak Age Groups</p>
            <p className="text-lg font-bold text-green-900">
              F: {highestFemaleGroup} | M: {highestMaleGroup}
            </p>
          </div>
        </div>

        {/* Table + Chart */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
          {/* Table */}
          <div className="flex flex-col justify-center">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden">
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="p-2 text-left">Age Group</th>
                    <th className="p-2" style={{ textAlign: "right" }}>
                      All Sexes
                    </th>
                    <th className="p-2" style={{ textAlign: "right" }}>
                      Female
                    </th>
                    <th className="p-2" style={{ textAlign: "right" }}>
                      Male
                    </th>
                    <th className="p-2" style={{ textAlign: "right" }}>
                      Female Share %
                    </th>
                    <th className="p-2" style={{ textAlign: "right" }}>
                      Male Share %
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((row, i) => (
                    <tr key={i} className="hover:bg-gray-100 transition p-2">
                      <td className="p-2 text-left">{row.age_group_label}</td>
                      <td
                        className="p-2"
                        style={{
                          textAlign: "right",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {Number(row.all_sexes_2024).toLocaleString("en-US")}
                      </td>
                      <td
                        className="font-bold text-blue-800 p-2"
                        style={{
                          textAlign: "right",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {Number(row.female_2024).toLocaleString("en-US")}
                      </td>
                      <td
                        className="font-bold text-red-800 p-2"
                        style={{
                          textAlign: "right",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {Number(row.male_2024).toLocaleString("en-US")}
                      </td>
                      <td
                        className="font-bold text-blue-800 p-2"
                        style={{
                          textAlign: "right",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {row.female_share_2024}%
                      </td>
                      <td
                        className="font-bold text-red-800 p-2"
                        style={{
                          textAlign: "right",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {row.male_share_2024}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[400px] w-full h-96 p-4">
              <Bar data={chartData} options={chartOptions} />
            </div>
          </div>
        </div>
      </div>

      {/* Download Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 text-white px-4 py-2 rounded-lg flex items-center gap-2 shadow-lg hover:scale-105 transition"
        >
          Download as PDF
          <img src={pdfIcon} alt="pdf-icon" className="w-[1.5rem]" />
        </button>
      </div>
    </>
  );
}
